import { CircleCheck, CircleX, ExternalLink } from "lucide-mui";
import Box from "@mui/material/Box";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";

/** Bold title with an external-link icon that opens without triggering the row click. */
export function TitleLinkCell({ title, href }: { title: ReactNode; href?: string | null }) {
  return (
    <TableCell>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {title}
        </Typography>
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink sx={{ fontSize: 14, color: "text.secondary" }} />
          </a>
        )}
      </Box>
    </TableCell>
  );
}

/** A check for a yes/no column (remote, verified, subdomain), a dash otherwise. */
export function CheckCell({
  checked,
  label,
  onFalse,
}: {
  /** null/undefined: unknown, shown as "-". */
  checked: boolean | null | undefined;
  label?: string;
  /** How a known `false` shows: "-" like unknown (default), a red cross (a check that failed) or "No". */
  onFalse?: "dash" | "cross" | "no";
}) {
  let content;
  if (checked) {
    content = <CircleCheck aria-label={label} sx={{ fontSize: 18, color: "success.main" }} />;
  } else if (checked === false && onFalse === "cross") {
    content = <CircleX aria-label={label ? `Not ${label.toLowerCase()}` : undefined} sx={{ fontSize: 18, color: "error.main" }} />;
  } else {
    content = (
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {checked === false && onFalse === "no" ? "No" : "-"}
      </Typography>
    );
  }
  return <TableCell>{content}</TableCell>;
}

/** A check when the record is matched to a company, a dash otherwise. */
export function MatchedCell({ matched }: { matched: boolean }) {
  return <CheckCell checked={matched} label="Matched" />;
}
